import React, { useEffect, useRef } from 'react';
import { useIPTV } from '../../context/IPTVContext';
import { useCastSender, openCastRemote } from './CastModal';

/**
 * V8.8.6 — Atende o "Enviar para a TV" da pergunta "Seu perfil está reproduzindo em outra tela":
 * quando o conteúdo escolhido já está no app, monta o pacote (canal + canais da categoria, episódio +
 * episódios, filme) e manda para a TV. Não desenha nada.
 */
export const CastBridge: React.FC = () => {
  const {
    castSendRequest,
    clearCastSendRequest,
    currentChannel,
    currentMovie,
    currentSeries,
    currentEpisode,
    getContentProgress,
    showToast,
    stopPlayback,
  } = useIPTV();
  const { buildPlayPayload, ensureFullEpisodes, postCommand } = useCastSender();
  const busyRef = useRef(false);

  useEffect(() => {
    if (!castSendRequest || busyRef.current) return;
    const hasContent = !!(currentChannel || currentMovie || (currentSeries && currentEpisode));
    if (!hasContent) return; // espera o conteúdo escolhido entrar no app

    // Filme/episódio: continua de onde parou (se não terminou)
    const id = currentMovie?.id || currentEpisode?.id || '';
    const pr = !currentChannel && id ? getContentProgress(id) : null;
    const startAt = pr && !pr.isCompleted ? pr.watchedSeconds : 0;
    busyRef.current = true;
    const req = castSendRequest;
    // Série: carrega a lista completa de episódios antes de mandar (o controle e a TV mostram a temporada)
    ensureFullEpisodes()
      .then((fullEps) => {
        const payload = buildPlayPayload(startAt, fullEps);
        if (!payload) throw new Error('sem_conteudo');
        return postCommand(req.sessionId, 'play', payload);
      })
      .then((data: any) => {
        if (data?.error === 'screen_limit') showToast(data.message || 'Limite de telas atingido.', 'warning');
        else if (data?.success) {
          showToast(`Enviado para a ${req.tvName}`, 'success');
          // Fecha o player deste aparelho (volta para a lista) e abre o controle
          stopPlayback();
          openCastRemote();
        }
        else showToast('Não foi possível enviar para a TV.', 'error');
      })
      .catch(() => showToast('Erro de conexão ao enviar para a TV.', 'error'))
      .finally(() => {
        busyRef.current = false;
        clearCastSendRequest();
      });
  }, [
    castSendRequest,
    currentChannel,
    currentMovie,
    currentSeries,
    currentEpisode,
    getContentProgress,
    buildPlayPayload,
    ensureFullEpisodes,
    postCommand,
    showToast,
    clearCastSendRequest,
    stopPlayback,
  ]);

  return null;
};
